import type { ActivityEvent, ActivityEventType } from '@/shared/types/activityEvent';
import { getResourceLink } from './resourceLinkRegistry';

export interface TransformedEvent {
  message: string;
  resourceLink: string;
  domainLabel: string;
  action: string;
}

/**
 * Event types that are displayed in the activity feed.
 * Other event types are still logged to activity_log for audit purposes
 * but are filtered out before rendering — they add noise without value
 * in the feed UI.
 */
const DISPLAYED_EVENT_TYPES = new Set<ActivityEventType>([
  // Workflow
  'workflow_spot.created',
  'hiring_spot.created',
  'hiring_spot.filled',
  'employee.added',
  'team.created',
  'team.changed',
  'workflow.assigned',
  // Goals
  'csf_info.updated',
  'csf_status.updated',
  'company_initiative.added',
  'company_initiative.updated',
  'portfolio.added',
  // CRM
  'key_moment.added',
  'contact.added',
  'company_review.added',
  // Billing
  'billing_cycle.archived',
  // Hiring
  'confirmed_hire.added',
  'confirmed_hire.connected',
  // Sales
  'audit.added',
]);

/**
 * Returns true if the event type should be displayed in the activity feed.
 * Use this to filter events before transforming/rendering them.
 */
export function isDisplayedEventType(eventType: ActivityEventType): boolean {
  return DISPLAYED_EVENT_TYPES.has(eventType);
}

/**
 * Extracts a human-readable action verb from the event type.
 * e.g., 'workflow_spot.created' -> 'created', 'employee.deactivated' -> 'deactivated'
 */
function extractAction(eventType: string): string {
  const parts = eventType.split('.');
  return parts[1] || 'updated';
}

/**
 * Formats a resource type into a readable label.
 * e.g., 'workflow_spot' -> 'workflow spot', 'hiring_spot' -> 'hiring spot'
 */
function formatResourceType(resourceType: string): string {
  return resourceType.replace(/_/g, ' ');
}

/**
 * Domain display labels
 */
const DOMAIN_LABELS: Record<string, string> = {
  workflow: 'Workflow',
  goals: 'Goals',
  billing: 'Billing',
  hiring: 'Hiring',
  crm: 'CRM',
  sales: 'Sales',
  intel: 'Intel',
  admin: 'Admin',
};

/**
 * Generates a human-readable message from structured event fields.
 * Uses event_type pattern matching to produce clear, contextual descriptions.
 */
function generateStructuredMessage(event: ActivityEvent): string {
  const { event_type, subject, context } = event;
  const action = extractAction(event_type);
  const resourceName = subject.resource_name;

  switch (event_type) {
    // Workflow spots
    case 'workflow_spot.created':
      return `Created workflow spot for ${context.team_name || 'team'}`;

    // Hiring spots
    case 'hiring_spot.created':
      return `Created '${resourceName}' hiring spot in ${context.team_name || 'team'}`;
    case 'hiring_spot.filled':
      return `Filled '${resourceName}' hiring spot in ${context.team_name || 'team'}`;

    // Employees
    case 'employee.added':
      return `Added ${resourceName}${context.program_name ? ` to ${context.program_name}'s program` : ''}`;

    // Teams
    case 'team.created':
      return `Created team '${resourceName}'${context.program_name ? ` in ${context.program_name}'s program` : ''}`;
    case 'team.changed':
      return `Moved team '${resourceName}'${context.program_name ? ` to ${context.program_name}'s program` : ''}`;

    // Workflow assignment
    case 'workflow.assigned':
      return `Assigned workflow '${resourceName}'${context.team_name ? ` to ${context.team_name}'s team` : ''}`;

    // Goals — CSF
    case 'csf_info.updated':
      return `Updated CSF info '${context.csf_name || resourceName}'${context.card_title ? ` in '${context.card_title}'` : ''}`;
    case 'csf_status.updated': {
      const statusPart = context.new_status ? ` to ${context.new_status}` : '';
      return `Updated CSF status '${context.csf_name || resourceName}'${statusPart}${context.card_title ? ` in '${context.card_title}'` : ''}`;
    }

    // Goals — Initiatives
    case 'company_initiative.added':
      return `Added company initiative '${context.initiative_title || resourceName}'`;
    case 'company_initiative.updated':
      return `Updated company initiative '${context.initiative_title || resourceName}'`;

    // Goals — Portfolios
    case 'portfolio.added':
      return `Added portfolio '${context.portfolio_name || resourceName}'`;

    // CRM
    case 'key_moment.added':
      return `Added key moment '${resourceName}'${context.company_name ? ` for ${context.company_name}` : ''}`;
    case 'contact.added':
      return `Added contact '${resourceName}'${context.company_name ? ` at ${context.company_name}` : ''}`;
    case 'company_review.added':
      return `Added review '${resourceName}'${context.company_name ? ` for ${context.company_name}` : ''}`;

    // Billing
    case 'billing_cycle.archived':
      return `Archived billing cycle '${resourceName}'`;

    // Hiring
    case 'confirmed_hire.added':
      return `Added confirmed hire '${resourceName}'${context.department ? ` in ${context.department}` : ''}`;
    case 'confirmed_hire.connected':
      return `Connected confirmed hire '${resourceName}' to hiring spot`;

    // Sales
    case 'audit.added':
      return `Added ${context.audit_type || 'audit'} '${resourceName}'`;

    default:
      break;
  }

  // Fallback: generate from action + resource type + name
  const readableType = formatResourceType(subject.resource_type);
  return `${action.charAt(0).toUpperCase() + action.slice(1)} ${readableType} '${resourceName}'`;
}

/**
 * Transforms a raw ActivityEvent into display-ready content for the UnifiedCard.
 *
 * Priority order:
 * 1. Use event.message if present and readable
 * 2. Generate from structured fields (event_type, subject, context)
 * 3. Fallback to generic "{action} {resource_type} '{name}'"
 */
export function transformEvent(event: ActivityEvent): TransformedEvent {
  const action = extractAction(event.event_type);
  const domainLabel = DOMAIN_LABELS[event.domain] || event.domain;

  // Priority 1: Use existing message if it's meaningful
  const message = event.message && event.message.trim().length > 0
    ? event.message
    : generateStructuredMessage(event);

  const resourceLink = getResourceLink(event);

  return {
    message,
    resourceLink,
    domainLabel,
    action,
  };
}
